import { spans } from './scheduleTimeline';

const MINUTE = 60000;
export const localDateTime = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
export const actualIso = local => {
  if (!local) return null;
  const date = new Date(local);
  if (!Number.isFinite(date.getTime()) || localDateTime(date.toISOString()).slice(0,16) !== local.slice(0,16))
    throw new Error('Choose a valid actual date and time.');
  return date.toISOString();
};
export const delayLabel = minutes => {
  const rounded = Math.round(minutes);
  return rounded === 0 ? 'On time' : `${Math.abs(rounded)} min ${rounded > 0 ? 'behind' : 'ahead'}`;
};
export const clockLabel = (timestamp, dayDate) => {
  if (timestamp == null || !Number.isFinite(timestamp)) return '—';
  const local = localDateTime(new Date(timestamp).toISOString());
  return `${local.slice(11,16)}${dayDate && local.slice(0,10) !== dayDate ? ` (${local.slice(0,10)})` : ''}`;
};

export const productionNow = (data, now = Date.now()) => {
  const server = Date.parse(data?.serverTime);
  return (Number.isFinite(server) && data?.receivedAt != null ? server + Math.max(0,now-data.receivedAt) : now) + (data?.clockOffsetMs || 0);
};
export const wrapDifference = minutes => {
  const amount = Math.abs(Math.round(minutes)), hours = Math.floor(amount/60), mins = amount%60;
  const text = [hours ? `${hours} hour${hours===1?'':'s'}` : '', mins ? `${mins} minute${mins===1?'':'s'}` : ''].filter(Boolean).join(' ');
  return amount === 0 ? '' : `${text} ${minutes>0?'behind schedule':'saved'}`;
};

// Completion-only projection: planned intervals define dependencies and parallel joins.
// No starts or inferred actual durations are written back to the schedule.
export const liveDay = (day, records = [], now = Date.now()) => {
  const planned = spans(day.entries || []);
  const byId = new Map(records.map(r => [r.occurrenceId,r]));
  const firstRecord = planned.map(s=>byId.get(s.e.id)).find(r=>r?.actualFinish||r?.actualStart);
  const midnight = new Date(`${day.date || localDateTime(firstRecord?.actualFinish || firstRecord?.actualStart || new Date(now).toISOString()).slice(0,10)}T00:00:00`).getTime();
  const atMinute = m => { const date=new Date(midnight); date.setMinutes(m); return date.getTime(); };
  const projections = [];
  const items = planned.map(s => {
    const record=byId.get(s.e.id);
    const actualStart=record?.actualStart ? Date.parse(record.actualStart) : null;
    const actualFinish=record?.actualFinish ? Date.parse(record.actualFinish) : null;
    const plannedStart=atMinute(s.from), plannedFinish=atMinute(s.to);
    const predecessors=projections.filter(p=>p.span.to<=s.from && p.span.from<s.from);
    let projectedStart=plannedStart;
    if(predecessors.length) {
      const boundary=Math.max(...predecessors.map(p=>p.span.to));
      projectedStart=Math.max(...predecessors.filter(p=>p.span.to===boundary).map(p=>p.finish))+(s.from-boundary)*MINUTE;
      projectedStart=Math.max(projectedStart,...predecessors.map(p=>p.finish));
    }
    // An incomplete current interval keeps its remaining planned work. Once its
    // finish passes, clock overruns propagate, never now + its full duration.
    const baselineFinish=projectedStart+(s.e.durationMin||0)*MINUTE;
    const projectedFinish=actualFinish ?? Math.max(baselineFinish, now>projectedStart ? now : baselineFinish);
    const behind=Math.max(0,(now-plannedFinish)/MINUTE,(projectedStart-plannedStart)/MINUTE);
    const status=actualFinish!=null?'completed':behind>0?'behind':now>=projectedStart?'active':'not-started';
    projections.push({span:s,finish:projectedFinish});
    return {id:s.e.id,record,status,plannedStart,plannedFinish,actualStart,actualFinish,
      duration:s.e.durationMin||0,progressDelta:actualFinish!=null?(actualFinish-plannedFinish)/MINUTE:behind,
      projectedStart,projectedFinish};
  });
  const completed=items.filter(i=>i.actualFinish!=null).length;
  const complete=items.length>0 && completed===items.length;
  const plannedWrap=planned.length?Math.max(...planned.map(s=>atMinute(s.to))):null;
  const actualWrap=complete?Math.max(...items.map(i=>i.actualFinish)):null;
  const estimatedWrap=complete?actualWrap:projections.length?Math.max(...projections.map(p=>p.finish)):null;
  return {items,plannedWrap,estimatedWrap,actualWrap,complete,completed,remaining:items.length-completed,
    delay:plannedWrap==null?0:(estimatedWrap-plannedWrap)/MINUTE,hasProgress:completed>0};
};
